import { mapActions, mapState } from 'vuex';
import alertBox from '@/components/generics/alert-box/alert-box.vue';
import accountPhoneUpdate from '@/components/account/account-phone-update/account-phone-update.vue';
import noDataDashedBox from '@/components/generics/no-data-dashed-box/no-data-dashed-box.vue';
import defaultInstitutionLogo from '@/assets/icons/bank.svg';
import createBankLinkDiagnostics from '@/helpers/bankLinkDiagnostics';
import aerosyncWidgetAdapter from './aerosync-widget-adapter';

const COMPLETION_RECONCILIATION_ATTEMPTS = 4;
const COMPLETION_RECONCILIATION_DELAY_MS = 1500;
const UNCERTAIN_COMPLETION_CODES = [
  'BANK_LINK_COMPLETION_FAILED', 'BANK_LINK_COMPLETION_TIMEOUT',
];
const PHONE_VERIFICATION_REQUIRED_REASON_CODE = 'BANK_LINK_PHONE_VERIFICATION_REQUIRED';
const PROVIDER_IDENTITY_ALREADY_LINKED_REASON_CODE =
  'BANK_LINK_PROVIDER_IDENTITY_ALREADY_LINKED';
const PROVIDER_CONFIRMATION_READY_REASON_CODE =
  'BANK_LINK_PROVIDER_CONFIRMATION_READY';
const PROVIDER_CONFIRMATION_REQUIRED_REASON_CODE =
  'BANK_LINK_PROVIDER_CONFIRMATION_REQUIRED';
const PROVIDER_CONFIRMATION_CODE_LENGTH = 6;

function wait(delayMs) {
  return new Promise(resolve => setTimeout(resolve, delayMs));
}

function canReconcileCompletion(result) {
  return !!result && result.retryable === true &&
    UNCERTAIN_COMPLETION_CODES.includes(result.reasonCode);
}

export default {
  components: {
    accountPhoneUpdate,
    alertBox,
    noDataDashedBox,
  },
  props: {
    purpose: {
      type: String,
      default: 'deposit',
      validator: value => ['deposit', 'withdrawal'].includes(value),
    },
    noticeText: {
      type: String,
      default: null,
    },
    selectable: {
      type: Boolean,
      default: false,
    },
  },
  data() {
    return {
      accountBeingRemoved: null,
      accounts: [],
      advisoryActionUrl: '',
      advisoryMessage: '',
      completionConnectionId: null,
      confirmationChannel: '',
      confirmationCode: '',
      confirmationDestination: '',
      confirmationErrorMessage: '',
      confirmationResendAvailableAt: null,
      confirmationSecondsUntilResend: 0,
      confirmationTimer: null,
      defaultInstitutionLogo,
      errorMessage: '',
      isCompletingLink: false,
      isDestroyed: false,
      isLinking: false,
      isLoading: true,
      isManagingAccounts: false,
      isPhoneVerificationRequired: false,
      isPhoneVerificationVisible: false,
      isProviderConfirmationReady: false,
      isProviderConfirmationRequired: false,
      isSendingProviderConfirmation: false,
      isConfirmingProviderUser: false,
      isRemovingAccount: false,
      isWidgetLoading: false,
      isWidgetReady: false,
      linkSession: null,
      linkPurpose: 'deposit',
      linkingAccountId: null,
      phoneRequirementActionText: 'Add Phone Number',
      phoneRequirementMessage: '',
      widgetControls: null,
      reportDiagnostic: null,
      widgetLaunchGeneration: 0,
      widgetElementId: `bank-link-widget-${Date.now()}-${Math.random().toString(36).slice(2)}`,
    };
  },
  computed: {
    ...mapState('user', ['userData']),
    bankLinkButtonText() {
      if (this.isCompletingReconnect) {
        return 'Finishing account reconnection...';
      }
      if (this.isCompletingLink) {
        return 'Finishing bank connection...';
      }
      if (this.isLinking) {
        return 'Finish connecting in Aerosync...';
      }
      if (this.isWidgetLoading) {
        return 'Loading Aerosync...';
      }
      return '';
    },
    confirmationPrompt() {
      if (this.confirmationChannel === 'sms' &&
        this.confirmationDestination) {
        return `Enter the 6-digit code Aerosync sent to ${this.confirmationDestination}.`;
      }
      if (this.confirmationChannel === 'email' &&
        this.confirmationDestination) {
        return `Enter the 6-digit code Aerosync sent to ${this.confirmationDestination}.`;
      }
      return 'Enter the 6-digit verification code sent by Aerosync.';
    },
    contentTitle() {
      if (this.isPhoneVerificationRequired) return 'PredictIt Phone Verification';
      if (this.isProviderConfirmationRequired ||
        this.isProviderConfirmationReady) return 'Aerosync Verification';
      return 'Select Account';
    },
    canSubmitProviderConfirmation() {
      return this.confirmationCode.length ===
        PROVIDER_CONFIRMATION_CODE_LENGTH &&
        !this.isConfirmingProviderUser &&
        !this.isSendingProviderConfirmation;
    },
    resendButtonText() {
      return this.confirmationSecondsUntilResend > 0
        ? `Resend code in ${this.confirmationSecondsUntilResend}s`
        : 'Resend Code';
    },
    completionProgressText() {
      if (this.isCompletingReconnect) {
        return "Aerosync has connected. We're securely finishing your account reconnection.";
      }
      return "Aerosync has connected. We're securely finishing your bank account setup.";
    },
    isBusy() {
      return this.isCompletingLink ||
        this.isLinking ||
        this.isRemovingAccount ||
        this.isSendingProviderConfirmation ||
        this.isConfirmingProviderUser ||
        this.isWidgetLoading;
    },
    isCompletingReconnect() {
      return this.isCompletingLink && this.linkingAccountId !== null;
    },
    isVerificationFlowActive() {
      return this.isPhoneVerificationRequired ||
        this.isProviderConfirmationReady ||
        this.isProviderConfirmationRequired;
    },
    featureNotice() {
      return this.noticeText === null ? '' : this.noticeText;
    },
    showAccountSkeleton() {
      return this.isLoading ||
        (this.isCompletingLink && !this.isCompletingReconnect);
    },
    showEmptyState() {
      return !this.showAccountSkeleton && this.accounts.length === 0;
    },
  },
  methods: {
    ...mapActions('wallet', [
      'completeBankLink',
      'getBankLinkCompletionStatus',
      'createBankLinkSession',
      'startBankLinkProviderConfirmation',
      'confirmBankLinkProviderUser',
      'createBankReconnectSession',
      'getLinkedBankAccounts',
      'removeLinkedBankAccount',
      'recordBankLinkDiagnostic',
    ]),
    accountDescription(account) {
      if (!account) return 'Bank account';
      const accountName = account.accountName || account.accountType || 'Bank account';
      return account.accountNumberLastFour
        ? `${accountName} - ${account.accountNumberLastFour}`
        : accountName;
    },
    accountIsReconnecting(account) {
      return this.isCompletingReconnect && account &&
        account.linkedBankAccountId === this.linkingAccountId;
    },
    accountIsSelectable(account) {
      return this.selectable && !this.isBusy && account &&
        account.connectionStatus === 'Connected';
    },
    accountRequiresReconnect(account) {
      return account &&
        (account.connectionStatus === 'ReauthenticationRequired' ||
          account.connectionStatus === 'Failed');
    },
    applyAccountsResponse(data) {
      this.accounts = data && data.linkedBankAccounts
        ? data.linkedBankAccounts
        : [];
      this.$emit('accounts', data);
    },
    loadWidget() {
      const generation = this.widgetLaunchGeneration;
      this.isWidgetLoading = true;
      this.reportDiagnostic('sdk_requested');
      return aerosyncWidgetAdapter.load()
        .then(() => {
          if (this.isDestroyed || generation !== this.widgetLaunchGeneration) return false;
          this.isWidgetReady = true;
          this.reportDiagnostic('sdk_ready');
          this.errorMessage = '';
          return true;
        })
        .catch(() => {
          if (this.isDestroyed || generation !== this.widgetLaunchGeneration) return false;
          this.isWidgetReady = false;
          this.reportDiagnostic('sdk_failed', 'WIDGET_SDK_LOAD_FAILED');
          this.errorMessage =
            'The secure bank-linking window could not be loaded. Please try again.';
          return false;
        })
        .then((isReady) => {
          if (!this.isDestroyed && generation === this.widgetLaunchGeneration) {
            this.isWidgetLoading = false;
          }
          return isReady;
        });
    },
    loadAccounts(options) {
      const owner = this.userData.userName;
      const isCurrent = () => !this.isDestroyed && this.userData.userName === owner;
      const settings = options || {};
      const showLoading = settings.showLoading !== false;
      const showError = settings.showError !== false;
      if (showLoading) {
        this.isLoading = true;
      }
      if (settings.clearError !== false) {
        this.errorMessage = '';
      }

      return this.getLinkedBankAccounts()
        .then((data) => {
          if (!isCurrent()) return null;
          if (!data || !data.success) {
            if (showError) {
              this.errorMessage = data && data.message
                ? data.message
                : 'Bank accounts could not be loaded.';
            }
            if (showLoading) {
              this.accounts = [];
            }
            return null;
          }
          this.applyAccountsResponse(data);
          return data;
        })
        .catch(() => {
          if (!isCurrent()) return null;
          if (showError) {
            this.errorMessage =
              'Bank accounts could not be loaded. Please try again.';
          }
          if (showLoading) {
            this.accounts = [];
          }
          return null;
        })
        .then((data) => {
          if (isCurrent() && showLoading) {
            this.isLoading = false;
          }
          return data;
        });
    },
    clearConfirmationTimer() {
      if (this.confirmationTimer) {
        clearInterval(this.confirmationTimer);
        this.confirmationTimer = null;
      }
    },
    updateConfirmationCountdown() {
      const availableAt = Date.parse(this.confirmationResendAvailableAt || '');
      this.confirmationSecondsUntilResend = isNaN(availableAt)
        ? 0
        : Math.max(0, Math.ceil((availableAt - Date.now()) / 1000));
      if (this.confirmationSecondsUntilResend === 0) {
        this.clearConfirmationTimer();
      }
    },
    startConfirmationTimer() {
      this.clearConfirmationTimer();
      this.updateConfirmationCountdown();
      if (this.confirmationSecondsUntilResend > 0) {
        this.confirmationTimer = setInterval(
          () => this.updateConfirmationCountdown(),
          1000,
        );
      }
    },
    resetProviderConfirmationState() {
      this.clearConfirmationTimer();
      this.confirmationChannel = '';
      this.confirmationCode = '';
      this.confirmationDestination = '';
      this.confirmationErrorMessage = '';
      this.confirmationResendAvailableAt = null;
      this.confirmationSecondsUntilResend = 0;
      this.isProviderConfirmationReady = false;
      this.isProviderConfirmationRequired = false;
      this.isSendingProviderConfirmation = false;
      this.isConfirmingProviderUser = false;
    },
    setProviderConfirmationState(session, isReady) {
      const wasRequired = this.isProviderConfirmationRequired;
      this.clearConfirmationTimer();
      this.isProviderConfirmationReady = isReady;
      this.isProviderConfirmationRequired = !isReady;
      this.isLinking = false;
      if (isReady) {
        this.confirmationCode = '';
        this.confirmationChannel = '';
        this.confirmationDestination = '';
        this.confirmationErrorMessage = '';
        this.confirmationResendAvailableAt = null;
      } else {
        this.confirmationChannel = session.confirmationChannel || '';
        this.confirmationDestination = session.confirmationDestination || '';
        this.confirmationResendAvailableAt =
          session.confirmationResendAvailableAt || null;
        if (!wasRequired ||
          session.reasonCode === PROVIDER_CONFIRMATION_REQUIRED_REASON_CODE) {
          this.confirmationCode = '';
        }
        this.confirmationErrorMessage =
          session.reasonCode === PROVIDER_CONFIRMATION_REQUIRED_REASON_CODE
            ? ''
            : (session.message || 'We could not verify that code.');
        this.startConfirmationTimer();
      }
      this.$nextTick(() => {
        if (this.isDestroyed) return;
        const target = isReady
          ? this.$refs.sendProviderCodeButton
          : this.$refs.providerConfirmationCode;
        if (target) target.focus();
      });
    },
    showPhoneRequirement(session) {
      this.resetProviderConfirmationState();
      this.phoneRequirementActionText =
        session.reasonCode === PROVIDER_IDENTITY_ALREADY_LINKED_REASON_CODE
          ? 'Use a Different Phone Number'
          : 'Add Phone Number';
      this.phoneRequirementMessage = session.message ||
        'To link a bank account, add and verify a mobile phone number on your PredictIt account.';
      this.isPhoneVerificationRequired = true;
      this.isPhoneVerificationVisible = false;
      this.isLinking = false;
      this.$nextTick(() => {
        if (!this.isDestroyed && this.$refs.addPhoneNumberButton) {
          this.$refs.addPhoneNumberButton.focus();
        }
      });
    },
    handleSessionResponse(session, options) {
      const settings = options || {};
      if (this.isDestroyed || !session) return null;
      if (!session.success) {
        if (session.reasonCode === PHONE_VERIFICATION_REQUIRED_REASON_CODE ||
          session.reasonCode === PROVIDER_IDENTITY_ALREADY_LINKED_REASON_CODE) {
          this.showPhoneRequirement(session);
          return null;
        }
        if (session.reasonCode === PROVIDER_CONFIRMATION_READY_REASON_CODE) {
          this.setProviderConfirmationState(session, true);
          return null;
        }
        if ((session.reasonCode || '').indexOf(
          'BANK_LINK_PROVIDER_CONFIRMATION_',
        ) === 0) {
          this.setProviderConfirmationState(session, false);
          return null;
        }
        if (settings.confirmationRequest) {
          this.confirmationErrorMessage = session.message ||
            'The verification request could not be completed. Please try again.';
        } else {
          this.errorMessage =
            session.message || 'Bank linking could not be started.';
        }
        this.isLinking = false;
        return null;
      }

      this.resetProviderConfirmationState();
      this.advisoryActionUrl = session.advisoryActionUrl || '';
      this.advisoryMessage = session.advisoryMessage || '';
      this.linkSession = session;
      this.isLinking = true;
      return this.launchWidgetSession(session);
    },
    launchWidgetSession(session) {
      this.destroyWidgetControls();
      this.widgetLaunchGeneration += 1;
      const generation = this.widgetLaunchGeneration;
      const purpose = this.linkPurpose;
      return aerosyncWidgetAdapter.launch({
        elementId: this.widgetElementId,
        session,
        onSuccess: result => this.handleWidgetSuccess(result, generation, purpose),
        onClose: () => this.handleWidgetClose(generation),
        onError: error => this.handleWidgetError(error, generation),
        onDiagnostic: this.reportDiagnostic,
      })
        .then((controls) => {
          if (this.isDestroyed || generation !== this.widgetLaunchGeneration) {
            if (controls && typeof controls.destroy === 'function') {
              controls.destroy();
            }
            return null;
          }
          this.widgetControls = controls;
          return controls;
        })
        .catch(() => {
          if (this.isDestroyed || generation !== this.widgetLaunchGeneration) {
            return null;
          }
          const linkedBankAccountId = this.linkingAccountId;
          this.errorMessage =
            'The secure bank-linking window could not be opened. Please try again.';
          this.isLinking = false;
          this.linkingAccountId = null;
          this.focusLinkOrigin(linkedBankAccountId);
          return null;
        });
    },
    destroyWidgetControls() {
      this.widgetLaunchGeneration += 1;
      const controls = this.widgetControls;
      this.widgetControls = null;
      if (controls && typeof controls.destroy === 'function') {
        controls.destroy();
      }
    },
    focusLinkOrigin(linkedBankAccountId) {
      this.$nextTick(() => {
        if (this.isDestroyed) return;
        let target = null;
        if (linkedBankAccountId) {
          target = this.$el.querySelector(
            `[data-linked-bank-account-id="${linkedBankAccountId}"]`,
          );
        }
        if (!target) target = this.$refs.addBankAccountButton;
        if (target) target.focus();
      });
    },
    addAccount(account) {
      if (this.isBusy) return null;
      if (!this.isWidgetReady) {
        const generation = this.widgetLaunchGeneration;
        return this.loadWidget()
          .then((isReady) => {
            if (isReady && !this.isDestroyed && generation === this.widgetLaunchGeneration) {
              return this.addAccount(account);
            }
            return null;
          });
      }

      this.destroyWidgetControls();
      this.resetProviderConfirmationState();
      this.reportDiagnostic = createBankLinkDiagnostics(this.recordBankLinkDiagnostic);
      this.reportDiagnostic('sdk_ready');
      this.completionConnectionId = null;
      this.advisoryActionUrl = '';
      this.advisoryMessage = '';
      this.errorMessage = '';
      this.isLinking = true;
      this.linkingAccountId = account
        ? account.linkedBankAccountId
        : null;

      // Keep direction with this attempt, including OTP and late widget callbacks.
      this.linkPurpose = this.purpose;
      const generation = this.widgetLaunchGeneration;
      const sessionRequest = account && account.linkedBankAccountId
        ? this.createBankReconnectSession({
          linkedBankAccountId: account.linkedBankAccountId,
          purpose: this.linkPurpose,
          clientAttemptId: this.reportDiagnostic.clientAttemptId,
        })
        : this.createBankLinkSession({
          purpose: this.linkPurpose, clientAttemptId: this.reportDiagnostic.clientAttemptId,
        });

      return sessionRequest
        .then(session => (generation === this.widgetLaunchGeneration
          ? this.handleSessionResponse(session) : null))
        .catch(() => {
          if (this.isDestroyed || generation !== this.widgetLaunchGeneration) return null;
          const linkedBankAccountId = this.linkingAccountId;
          this.errorMessage =
            'Bank linking could not be started. Please try again.';
          this.isLinking = false;
          this.linkingAccountId = null;
          this.focusLinkOrigin(linkedBankAccountId);
          return null;
        });
    },
    sendProviderVerificationCode(resend) {
      if (this.isSendingProviderConfirmation ||
        (resend && this.confirmationSecondsUntilResend > 0)) {
        return null;
      }

      this.isSendingProviderConfirmation = true;
      const generation = this.widgetLaunchGeneration;
      this.confirmationErrorMessage = '';
      return this.startBankLinkProviderConfirmation({
        resend: resend === true,
        purpose: this.linkPurpose,
        clientAttemptId: this.reportDiagnostic.clientAttemptId,
      })
        .then((session) => {
          if (this.isDestroyed || generation !== this.widgetLaunchGeneration) return null;
          this.isSendingProviderConfirmation = false;
          if (resend && session &&
            session.reasonCode === PROVIDER_CONFIRMATION_REQUIRED_REASON_CODE) {
            this.confirmationCode = '';
          }
          return this.handleSessionResponse(session, {
            confirmationRequest: true,
          });
        })
        .catch(() => {
          if (!this.isDestroyed && generation === this.widgetLaunchGeneration) {
            this.isSendingProviderConfirmation = false;
            this.confirmationErrorMessage =
              'The verification code could not be sent. Please try again.';
          }
          return null;
        });
    },
    confirmProviderVerificationCode() {
      if (!this.canSubmitProviderConfirmation) return null;
      this.isConfirmingProviderUser = true;
      const generation = this.widgetLaunchGeneration;
      this.confirmationErrorMessage = '';
      return this.confirmBankLinkProviderUser({
        code: this.confirmationCode,
        purpose: this.linkPurpose,
        clientAttemptId: this.reportDiagnostic.clientAttemptId,
      })
        .then((session) => {
          if (this.isDestroyed || generation !== this.widgetLaunchGeneration) return null;
          this.isConfirmingProviderUser = false;
          return this.handleSessionResponse(session, {
            confirmationRequest: true,
          });
        })
        .catch(() => {
          if (!this.isDestroyed && generation === this.widgetLaunchGeneration) {
            this.isConfirmingProviderUser = false;
            this.confirmationErrorMessage =
              'We could not verify that code. Please try again.';
          }
          return null;
        });
    },
    handleProviderConfirmationInput(event) {
      const value = (event.target.value || '')
        .replace(/[^0-9]/g, '')
        .slice(0, PROVIDER_CONFIRMATION_CODE_LENGTH);
      this.confirmationCode = value;
      event.target.value = value;
    },
    cancelProviderConfirmation() {
      this.resetProviderConfirmationState();
      this.errorMessage = '';
      this.focusLinkOrigin(null);
    },
    handleWidgetError(error, generation) {
      if (generation !== this.widgetLaunchGeneration ||
        this.isDestroyed || this.completionConnectionId) return;
      if (error && error.code === 'WIDGET_LOAD_TIMEOUT') {
        const linkedBankAccountId = this.linkingAccountId;
        this.widgetControls = null;
        this.isLinking = false;
        this.linkingAccountId = null;
        this.errorMessage =
          'The secure bank-linking window took too long to load. Please try again.';
        this.focusLinkOrigin(linkedBankAccountId);
      }
    },
    showPhoneVerification() {
      this.isPhoneVerificationVisible = true;
      this.$nextTick(() => {
        if (this.isDestroyed) return;
        const phoneInput = this.$el.querySelector('.account-phone-update__input');
        if (phoneInput) phoneInput.focus();
      });
    },
    handlePhoneVerificationCancel() {
      this.isPhoneVerificationRequired = false;
      this.isPhoneVerificationVisible = false;
      this.phoneRequirementActionText = 'Add Phone Number';
      this.phoneRequirementMessage = '';
      this.$nextTick(() => {
        if (!this.isDestroyed && this.$refs.addBankAccountButton) {
          this.$refs.addBankAccountButton.focus();
        }
      });
    },
    handlePhoneVerified() {
      if (this.isDestroyed) return null;
      this.isPhoneVerificationRequired = false;
      this.isPhoneVerificationVisible = false;
      this.phoneRequirementActionText = 'Add Phone Number';
      this.phoneRequirementMessage = '';
      return this.addAccount();
    },
    handleWidgetClose(generation) {
      if (generation !== this.widgetLaunchGeneration ||
        this.isDestroyed || this.completionConnectionId) return;
      const linkedBankAccountId = this.linkingAccountId;
      this.widgetControls = null;
      this.isLinking = false;
      this.linkingAccountId = null;
      this.focusLinkOrigin(linkedBankAccountId);
    },
    handleWidgetSuccess(result, generation, purpose) {
      if (generation !== this.widgetLaunchGeneration || this.isDestroyed) return;
      const connectionId = result && result.connectionId;
      const userReference = (result && result.userReference) ||
        (this.linkSession && this.linkSession.userReference);
      if (!connectionId || !userReference) {
        const linkedBankAccountId = this.linkingAccountId;
        this.destroyWidgetControls();
        this.errorMessage =
          'The bank-linking window returned an incomplete result.';
        this.isLinking = false;
        this.linkingAccountId = null;
        this.focusLinkOrigin(linkedBankAccountId);
        return;
      }
      if (this.completionConnectionId === connectionId) {
        return;
      }

      const linkedBankAccountId = this.linkingAccountId;
      this.completionConnectionId = connectionId;
      this.destroyWidgetControls();
      const completionGeneration = this.widgetLaunchGeneration;
      const isCurrent = () => !this.isDestroyed &&
        this.widgetLaunchGeneration === completionGeneration &&
        this.completionConnectionId === connectionId;
      const reference = {
        connectionId,
        userReference,
        purpose,
        clientAttemptId: this.reportDiagnostic.clientAttemptId,
      };
      this.errorMessage = '';
      this.isCompletingLink = true;
      this.isLinking = true;
      this.reportDiagnostic('completion_requested');

      const recover = () => this.reconcileCompletion(
        reference, completionGeneration, COMPLETION_RECONCILIATION_ATTEMPTS,
      );
      this.completeBankLink(reference)
        .then((completion) => {
          if (!isCurrent()) return false;
          if (completion && completion.success === true &&
            Array.isArray(completion.linkedBankAccounts)) {
            this.accounts = completion.linkedBankAccounts;
            this.reportDiagnostic('completion_succeeded');
            return true;
          }
          // A provider-declared failure is definitive. Only a missing response, the
          // server's catch-all or its own timeout (either may follow a persisted
          // completion) may recover through the read-only status check.
          const ambiguous = !completion || typeof completion.success !== 'boolean' ||
            canReconcileCompletion(completion);
          if (!ambiguous) {
            this.errorMessage = completion.message || 'The bank account could not be linked.';
            return false;
          }
          return recover();
        })
        .catch((error) => {
          if (!isCurrent()) return false;
          const response = error && error.response;
          const data = response && response.data;
          if (!canReconcileCompletion(data) &&
            ((data && data.success === false) || (response && response.status < 500))) {
            this.errorMessage = (data && data.message) ||
              'The bank account could not be linked. Please try again.';
            return false;
          }
          return recover();
        })
        .then((completed) => {
          if (!isCurrent()) return;
          this.isCompletingLink = false;
          this.isLinking = false;
          this.linkingAccountId = null;
          if (completed) {
            this.errorMessage = '';
            this.accountBeingRemoved = null;
            this.isManagingAccounts = false;
          } else {
            this.reportDiagnostic('completion_failed');
            this.errorMessage = this.errorMessage ||
              'The bank account could not be linked. Please try again.';
            this.focusLinkOrigin(linkedBankAccountId);
          }
        });
    },
    reconcileCompletion(reference, generation, attemptsRemaining) {
      const isCurrent = () => !this.isDestroyed &&
        this.widgetLaunchGeneration === generation &&
        this.completionConnectionId === reference.connectionId;
      if (!isCurrent()) return Promise.resolve(false);
      this.reportDiagnostic('reconciliation_requested');
      return this.getBankLinkCompletionStatus(reference)
        .then((status) => {
          if (!isCurrent() || !status || status.completed !== true ||
            !(Number(status.linkedBankAccountId) > 0)) return false;
          return this.getLinkedBankAccounts({ timeout: 5000 }).then((data) => {
            if (!isCurrent() || !data || data.success !== true) return false;
            const accounts = data.linkedBankAccounts || [];
            const matched = accounts.some(account =>
              Number(account.linkedBankAccountId) === Number(status.linkedBankAccountId) &&
              account.connectionStatus === 'Connected');
            if (matched) {
              this.applyAccountsResponse(data);
              this.reportDiagnostic('completion_recovered');
            }
            return matched;
          });
        })
        .catch(() => false)
        .then((completed) => {
          if (completed || attemptsRemaining <= 1 || !isCurrent()) {
            if (!completed) this.reportDiagnostic('reconciliation_failed');
            return completed;
          }
          return wait(COMPLETION_RECONCILIATION_DELAY_MS)
            .then(() => this.reconcileCompletion(reference, generation, attemptsRemaining - 1));
        });
    },
    enterManageAccounts() {
      if (this.isBusy) return;
      this.accountBeingRemoved = null;
      this.isManagingAccounts = true;
    },
    selectAccount(account) {
      if (!this.accountIsSelectable(account)) return;
      this.$emit('select', account);
    },
    handleManageBack() {
      if (this.isRemovingAccount) return;
      if (this.accountBeingRemoved) {
        this.accountBeingRemoved = null;
        return;
      }
      this.isManagingAccounts = false;
    },
    requestRemoveAccount(account) {
      if (this.isBusy || !account) return;
      this.accountBeingRemoved = account;
    },
    confirmRemoveAccount() {
      const account = this.accountBeingRemoved;
      if (!account || !account.linkedBankAccountId || this.isRemovingAccount) {
        return null;
      }

      this.isRemovingAccount = true;
      const owner = this.userData.userName;
      const isCurrent = () => !this.isDestroyed && this.userData.userName === owner;
      this.errorMessage = '';
      return this.removeLinkedBankAccount({
        linkedBankAccountId: account.linkedBankAccountId,
      })
        .then((result) => {
          if (!isCurrent()) return null;
          if (!result.success) {
            this.errorMessage =
              result.message || 'The bank account could not be removed.';
            return null;
          }
          this.accounts = this.accounts.filter(existingAccount =>
            existingAccount.linkedBankAccountId !==
              account.linkedBankAccountId);
          this.accountBeingRemoved = null;
          if (this.accounts.length === 0) {
            this.isManagingAccounts = false;
          }
          return this.loadAccounts({
            clearError: false,
            showError: false,
            showLoading: false,
          });
        })
        .catch(() => {
          if (!isCurrent()) return null;
          this.errorMessage =
            'The bank account could not be removed. Please try again.';
          return null;
        })
        .then((result) => {
          if (isCurrent()) this.isRemovingAccount = false;
          return result;
        });
    },
  },
  created() {
    this.reportDiagnostic = createBankLinkDiagnostics(this.recordBankLinkDiagnostic);
    this.loadAccounts();
  },
  mounted() {
    this.loadWidget();
  },
  beforeDestroy() {
    this.isDestroyed = true;
    this.clearConfirmationTimer();
    this.destroyWidgetControls();
  },
};



// WEBPACK FOOTER //
// ./src/components/wallet/bank-links/bank-links.js